import { atom, useSetAtom } from 'jotai'
import { useEffect } from 'react'
import type { RestaurantTagId } from '../../../../lib/graphql/scalars'
import { afternoonTea } from '../../sd/preferences/tags'
import type { Tag } from '../../sd/preferences/tags'
import type { TagType } from '../tags'
import type { Facet } from './type'

export function tagToFacet(tag: Tag): Facet {
  return {
    label: tag.label,
    facetKey: tag.facetKey,
    selected(criteria) {
      const tagType = toTagType(tag.facetKey)
      return criteria?.[tagType]?.includes(toTagId(tag.facetKey)) ?? false
    },
    showInList(criteria) {
      return this.selected(criteria)
    },
    add(current) {
      const tagType = toTagType(tag.facetKey)
      const tags = new Set(current?.[tagType] ?? [])
      tags.add(toTagId(tag.facetKey))
      return { ...current, [tagType]: Array.from(tags).toSorted() }
    },
    remove(current) {
      const tagType = toTagType(tag.facetKey)
      const tags = new Set(current?.[tagType] ?? [])
      tags.delete(toTagId(tag.facetKey))
      if (tags.size === 0) {
        const { [tagType]: _, ...removed } = current ?? {}
        return removed
      }
      return { ...current, [tagType]: Array.from(tags).toSorted() }
    },
  }
}

function toTagType(facetKey: string): TagType {
  if (facetKey.startsWith('tag.tag_scene.')) {
    return 'sceneTagIds'
  } else if (facetKey.startsWith('tag.tag_ingredient.')) {
    return 'foodTagIds'
  } else if (facetKey.startsWith('tag.tag_genre.')) {
    return 'genreTagIds'
  } else if (facetKey.startsWith('tag.tag_drink.')) {
    return 'drinkTagIds'
  } else {
    throw new Error(`Unknown tag type: ${facetKey}`)
  }
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest
  describe('toTagType', () => {
    it('シーン - アフタヌーンティー', () => {
      expect(toTagType('tag.tag_scene.2981')).toBe('sceneTagIds')
    })
    it('食材 - 伊勢海老', () => {
      expect(toTagType('tag.tag_ingredient.1877')).toBe('foodTagIds')
    })
    it('ジャンル - シュラスコ', () => {
      expect(toTagType('tag.tag_genre.84')).toBe('genreTagIds')
    })
    it('飲み物 - スパークリングワイン', () => {
      expect(toTagType('tag.tag_drink.238')).toBe('drinkTagIds')
    })
    it('不明なタグ', () => {
      expect(() => toTagType('tag.tag_food.123')).toThrowError()
    })
  })
}

function toTagId(facetKey: string): RestaurantTagId {
  return facetKey.split('.').at(-1) as RestaurantTagId
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest
  describe('toTagId', () => {
    it('アフタヌーンティー', () => {
      expect(toTagId('tag.tag_scene.2981')).toBe('2981')
    })
  })
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest
  describe('TagFacet', () => {
    const facet = tagToFacet(afternoonTea)
    it('selected', () => {
      expect(facet.selected({ sceneTagIds: ['2981'] as RestaurantTagId[] })).toBe(true)
      expect(facet.selected({ sceneTagIds: ['2982'] as RestaurantTagId[] })).toBe(false)
      expect(facet.selected({})).toBe(false)
    })
    it('add (empty)', () => {
      expect(facet.add({})).toStrictEqual({ sceneTagIds: ['2981'] })
    })
    it('add (others already exists)', () => {
      expect(facet.add({ sceneTagIds: ['2842'] as RestaurantTagId[] })).toStrictEqual({
        sceneTagIds: ['2842', '2981'],
      })
    })
    it('remove (will be empty)', () => {
      expect(facet.remove({ sceneTagIds: ['2981'] as RestaurantTagId[] })).toStrictEqual({})
    })
    it('remove (others will exists)', () => {
      expect(facet.remove({ sceneTagIds: ['2842', '2981'] as RestaurantTagId[] })).toStrictEqual({
        sceneTagIds: ['2842'],
      })
    })
  })
}

export type TagMap = Record<string, Tag>

export const tagMap$ = atom<TagMap>({})
tagMap$.debugLabel = 'tagMap$'

export function useSetTagMap(tags: TagMap) {
  const set = useSetAtom(tagMap$)
  useEffect(() => {
    set((current) => {
      // FIXME: current や tags が undefined が入る場合があるのでケア
      //        本来は root.tsx の typeof loader の型を修正すべき
      //        https://github.com/ikyucom/macaron-frontend/pull/3887#discussion_r1751090193
      return Object.fromEntries([...Object.entries(current ?? {}), ...Object.entries(tags ?? {})])
    })
  }, [tags, set])
}
